/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import React, { useMemo, useCallback, useRef, useState } from 'react'
import { useMyScheduleContext } from 'src/context/MyScheduleContext'
import RangePicker from 'src/components/ts-components/RangePicker'
import Text from 'src/components/ts-components/Text'
import TimePeriodFilter from '../TimePeriodFilter'
import MembershipTypeFilter from '../MembershipTypeFilter'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import cn from 'classnames'
import { AutoComplete, Spin, TreeSelect } from 'antd'
import {
  paragraphSmall,
  paragraphXSmall,
  weightRegular,
} from 'src/components/css'
import {
  transformToTreeSelectData,
  findNodeById,
  treeNodeToOrganization,
  getFirstLevelKeys,
} from '../../utils/treeSelectUtils'

const ALL_VALUE = 'all'
const SCROLL_THRESHOLD = 50
const DEBOUNCE_MS = 500
const ORG_PLACEHOLDER =
  '/images/generic_placeholders/placeholder_organization.png'
const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'
const COMPETITION_PLACEHOLDER =
  '/images/generic_placeholders/placeholder_competition.png'

const filterAutoComplete = css`
  ${paragraphSmall}
  ${weightRegular}
  width: 100%;
  height: 40px !important;
  color: ${colors.WHITE};

  .ant-select-selector {
    background: transparent !important;
    border-radius: 8px !important;
    height: 40px !important;
  }

  .ant-select-selection-search {
    display: flex;
    align-items: center;
  }

  .ant-select-selection-search-input {
    height: 38px !important;
    color: ${colors.WHITE} !important;
    text-overflow: ellipsis !important;
    overflow: hidden !important;
    white-space: nowrap !important;
  }

  .ant-select-selection-placeholder {
    line-height: 38px !important;
    font-size: 16px !important;
    color: ${colors.NEUTRAL_50} !important;
  }

  .ant-select-clear {
    color: ${colors.WHITE};
  }

  &.ant-select-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const filterTreeSelect = css`
  ${paragraphSmall}
  ${weightRegular}
  width: 100%;
  height: 40px !important;
  color: ${colors.WHITE};

  .ant-select-dropdown {
    border: 1px solid red !important;
  }

  .ant-select-selector {
    background: transparent !important;
    border-radius: 8px !important;
    height: 40px !important;
  }

  .ant-select-selection-search-input {
    height: 38px !important;
    color: ${colors.WHITE} !important;
  }

  .ant-select-selection-item {
    font-size: 13px !important;
    line-height: 38px !important;
    color: ${colors.WHITE} !important;
  }

  .ant-select-selection-placeholder {
    line-height: 38px !important;
    font-size: 16px !important;
    color: ${colors.NEUTRAL_50} !important;
  }

  .ant-select-clear {
    color: ${colors.WHITE};
  }

  &.ant-select-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const treeNodeStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  min-height: 36px;
`

const treeNodeImageStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid ${colors.NEUTRAL_50};
  align-self: center;
`

const optionImageStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid ${colors.NEUTRAL_50};
`

const treeNodeTextStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 1;
`

const treeNodeNameStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.WHITE};
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const treeNodeRoleStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
  line-height: 1;
  color: ${colors.NEUTRAL_50};
  display: none;
`

const sortOrderContainer = css`
  display: flex;
  align-items: center;
  gap: 0;
  background-color: transparent;
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  height: 40px;
  @media (max-width: 768px) {
    width: 100%;
  }
  align-self: flex-end;
`

const sortOrderButtonBase = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 40px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_0};

  &:first-of-type {
    border-radius: 8px 0 0 8px;
  }

  &:last-of-type {
    border-radius: 0 8px 8px 0;
  }

  &:hover:not(.sorted) {
    background-color: ${colors.SECONDARY_200};
  }

  &:active {
    transform: scale(0.98);
  }

  &.sorted {
    background-color: ${colors.SECONDARY_300};
    font-weight: 700;
  }

  &:not(.sorted) {
    background-color: transparent;
    font-weight: 400;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

/**
 * Format a date range for display
 */
const formatDateRange = (startsAt: string, endsAt: string) => {
  const start = dayjs(startsAt).format('MMM D, YYYY')
  const end = dayjs(endsAt).format('MMM D, YYYY')
  return `${start} - ${end}`
}

/**
 * FilterBar component
 * Displays all filters for the My Schedule page in a horizontal layout
 */
const FilterBar: React.FC = () => {
  const {
    // Filter selections
    selectedOrganization,
    selectedSchedule,
    selectedTeam,
    selectedVenue,
    selectedFacility,
    selectedDateRange,
    selectedTimePeriod,
    selectedMembershipType,
    selectedSortOrder,

    // Filter options
    organizationsTree,
    schedules,
    teams,
    venues,

    // Pagination state
    schedulesPagination,
    teamsPagination,
    venuesPagination,

    // Appending states
    appendingSchedules,
    appendingTeams,
    appendingVenues,

    // Loading states
    loadingOrganizations,
    loadingSchedules,
    loadingTeams,
    loadingVenues,

    // Setters
    setSelectedOrganization,
    setSelectedSchedule,
    setSelectedTeam,
    setSelectedVenue,
    setSelectedFacility,
    setSelectedDateRange,
    setSelectedTimePeriod,
    setSelectedMembershipType,
    setSelectedSortOrder,

    // Pagination methods
    fetchMoreSchedules,
    fetchMoreTeams,
    fetchMoreVenues,

    // Search methods
    searchSchedules,
    searchTeams,
    searchVenues,

    gamesPagination,
    datesLoaded,
  } = useMyScheduleContext()

  // Debounce refs for search
  const schedulesSearchTimeoutRef = useRef<any>(null)
  const teamsSearchTimeoutRef = useRef<any>(null)
  const venuesSearchTimeoutRef = useRef<any>(null)

  // Dropdown open state
  const [schedulesDropdownOpen, setSchedulesDropdownOpen] = useState(false)
  const [teamsDropdownOpen, setTeamsDropdownOpen] = useState(false)
  const [venuesDropdownOpen, setVenuesDropdownOpen] = useState(false)

  // Flags to detect when filters should be disabled (no items available)
  const shouldDisableOrganization = useMemo(() => {
    if (!organizationsTree) return true
    const countNodes = (nodes: any[]): number => {
      return nodes.reduce((count, node) => {
        return count + 1 + (node.children ? countNodes(node.children) : 0)
      }, 0)
    }
    return countNodes(organizationsTree) === 0
  }, [organizationsTree])

  const shouldDisableSchedule = schedules.length === 0
  const shouldDisableTeam = teams.length === 0
  const shouldDisableVenue = venues.length === 0
  const shouldDisableFacility =
    !selectedVenue || !selectedVenue.facilities?.length

  // Check if there are more pages to load
  const hasMoreSchedules =
    schedulesPagination &&
    schedulesPagination.current_page < schedulesPagination.total_pages
  const hasMoreTeams =
    teamsPagination &&
    teamsPagination.current_page < teamsPagination.total_pages
  const hasMoreVenues =
    venuesPagination &&
    venuesPagination.current_page < venuesPagination.total_pages

  // Scroll handlers for infinite scroll
  const handleSchedulesScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight <
        SCROLL_THRESHOLD
      if (nearBottom && hasMoreSchedules && !appendingSchedules) {
        fetchMoreSchedules()
      }
    },
    [hasMoreSchedules, appendingSchedules, fetchMoreSchedules]
  )

  const handleTeamsScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight <
        SCROLL_THRESHOLD
      if (nearBottom && hasMoreTeams && !appendingTeams) {
        fetchMoreTeams()
      }
    },
    [hasMoreTeams, appendingTeams, fetchMoreTeams]
  )

  // Debounced search handlers
  const handleSchedulesSearch = useCallback(
    (value: string) => {
      if (schedulesSearchTimeoutRef.current) {
        clearTimeout(schedulesSearchTimeoutRef.current)
      }
      // Only search if there's a value - empty string triggers are ignored
      if (value) {
        schedulesSearchTimeoutRef.current = setTimeout(() => {
          searchSchedules(value)
        }, DEBOUNCE_MS)
      }
    },
    [searchSchedules]
  )

  const handleTeamsSearch = useCallback(
    (value: string) => {
      if (teamsSearchTimeoutRef.current) {
        clearTimeout(teamsSearchTimeoutRef.current)
      }
      // Only search if there's a value - empty string triggers are ignored
      if (value) {
        teamsSearchTimeoutRef.current = setTimeout(() => {
          searchTeams(value)
        }, DEBOUNCE_MS)
      }
    },
    [searchTeams]
  )

  const handleVenuesScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight <
        SCROLL_THRESHOLD
      if (nearBottom && hasMoreVenues && !appendingVenues) {
        fetchMoreVenues()
      }
    },
    [hasMoreVenues, appendingVenues, fetchMoreVenues]
  )

  const handleVenuesSearch = useCallback(
    (value: string) => {
      if (venuesSearchTimeoutRef.current) {
        clearTimeout(venuesSearchTimeoutRef.current)
      }
      // Only search if there's a value - empty string triggers are ignored
      if (value) {
        venuesSearchTimeoutRef.current = setTimeout(() => {
          searchVenues(value)
        }, DEBOUNCE_MS)
      }
    },
    [searchVenues]
  )

  // Display value helpers for AutoComplete
  const getScheduleDisplayValue = useCallback(() => {
    if (!selectedSchedule) {
      return isCanlan ? 'All Seasons' : 'All Competitions'
    }
    return selectedSchedule.name_full || selectedSchedule.name
  }, [selectedSchedule])

  const getTeamDisplayValue = useCallback(() => {
    if (!selectedTeam) {
      return shouldDisableTeam ? 'No Teams' : 'All Teams'
    }
    return selectedTeam.name_full || selectedTeam.name
  }, [selectedTeam, shouldDisableTeam])

  const getVenueDisplayValue = useCallback(() => {
    if (!selectedVenue) {
      return 'All Locations'
    }
    return selectedVenue.name
  }, [selectedVenue])

  const getFacilityDisplayValue = useCallback(() => {
    if (!selectedFacility) {
      return 'All Surfaces'
    }
    return selectedFacility.name
  }, [selectedFacility])

  // Dropdown render for loading state
  const getDropdownRender = useCallback(
    (menu: React.ReactElement, loading: boolean) => (
      <>
        {menu}
        {loading && (
          <div
            css={css`
              display: flex;
              justify-content: center;
              padding: 8px;
            `}
          >
            <Spin size='small' />
          </div>
        )}
      </>
    ),
    []
  )

  // Transform data for TreeSelect (Organizations)
  const organizationsTreeData = useMemo(() => {
    if (!organizationsTree) {
      return []
    }

    const allOption = {
      value: ALL_VALUE,
      title: isCanlan ? 'All Leagues' : 'All Organizations',
      label: isCanlan ? 'All Leagues' : 'All Organizations',
      isLeaf: true,
    }

    return [allOption, ...transformToTreeSelectData(organizationsTree)]
  }, [organizationsTree])

  // Default expanded keys (first level)
  const defaultExpandedKeys = useMemo(() => {
    if (!organizationsTree) {
      return []
    }
    return getFirstLevelKeys(organizationsTree)
  }, [organizationsTree])

  // Custom tree node renderer with image
  const renderTreeNode = useCallback((nodeData: any) => {
    // "All" option doesn't have data - but still needs consistent padding
    if (!nodeData.data) {
      return (
        <div css={treeNodeStyle}>
          <span css={treeNodeNameStyle}>{nodeData.title}</span>
        </div>
      )
    }

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      e.currentTarget.onerror = null
      e.currentTarget.src = ORG_PLACEHOLDER
    }

    return (
      <div css={treeNodeStyle}>
        <img
          src={nodeData.data.image_url || ORG_PLACEHOLDER}
          alt={nodeData.data.name}
          css={treeNodeImageStyle}
          onError={handleImageError}
          loading='lazy'
        />
        <div css={treeNodeTextStyle}>
          <span css={treeNodeNameStyle}>{nodeData.title}</span>
          {nodeData.data.membership?.role_name && (
            <span css={treeNodeRoleStyle}>
              {nodeData.data.membership.role_name}
            </span>
          )}
        </div>
      </div>
    )
  }, [])

  // Transform data for AutoComplete options (Schedules, Teams)
  const scheduleOptions = useMemo(
    () => [
      {
        label: isCanlan ? 'All Seasons' : 'All Competitions',
        value: ALL_VALUE,
      },
      ...schedules.map((schedule) => {
        const orgName =
          schedule.organization?.name || schedule.organization?.name_full || ''
        const dateRange =
          schedule.starts_at && schedule.ends_at
            ? formatDateRange(schedule.starts_at, schedule.ends_at)
            : ''
        const displayName = schedule.name_full || schedule.name

        return {
          value: schedule.id,
          searchText: `${displayName} ${orgName}`.toLowerCase(),
          label: (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <img
                src={schedule.image?.url || COMPETITION_PLACEHOLDER}
                alt={displayName}
                css={optionImageStyle}
                onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = COMPETITION_PLACEHOLDER
                }}
                loading='lazy'
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  min-width: 0;
                  flex: 1;
                  gap: 2px;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.NEUTRAL_0}
                  style={'line-height: 1;'}
                >
                  {displayName}
                </Text>
                {(orgName || dateRange) && (
                  <Text
                    variant='paragraphXSmall'
                    color={colors.NEUTRAL_50}
                    style={'line-height: 1;'}
                  >
                    {orgName}
                    {orgName && dateRange ? ` (${dateRange})` : dateRange}
                  </Text>
                )}
              </div>
            </div>
          ),
        }
      }),
    ],
    [schedules]
  )

  const teamOptions = useMemo(
    () => [
      { label: shouldDisableTeam ? 'No Teams' : 'All Teams', value: ALL_VALUE },
      ...teams.map((team) => {
        const displayName = team.name_full || team.name
        return {
          value: team.id,
          label: (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <img
                src={team.image?.url || TEAM_PLACEHOLDER}
                alt={displayName}
                css={optionImageStyle}
                onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = TEAM_PLACEHOLDER
                }}
                loading='lazy'
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                  min-width: 0;
                  flex: 1;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                  style={'line-height: 1;'}
                >
                  {displayName}
                </Text>
                {team.organization?.name && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                    style={'line-height: 1;'}
                  >
                    {team.organization.name}
                  </Text>
                )}
              </div>
            </div>
          ),
        }
      }),
    ],
    [teams]
  )

  const venueOptions = useMemo(
    () => [
      { label: 'All Locations', value: ALL_VALUE, searchText: 'All Locations' },
      ...venues.map((venue) => {
        const addressParts: string[] = []
        if (venue?.address?.street_1) {
          addressParts.push(venue?.address?.street_1 || '')
        }
        if (venue?.address?.city) {
          addressParts.push(venue?.address?.city || '')
        }
        if (venue?.address?.country?.name) {
          addressParts.push(venue?.address?.country?.name || '')
        }
        const addressString = addressParts.join(', ')
        return {
          value: venue.id,
          searchText: venue.name,
          label: (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0;
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.WHITE}
              >
                {venue.name}
              </Text>
              {addressString && (
                <Text
                  variant='paragraphXSmall'
                  color={colors.NEUTRAL_50}
                  style={'line-height: 1;'}
                >
                  Surfaces: {venue?.facilities?.length || 0} • {addressString}
                </Text>
              )}
            </div>
          ),
        }
      }),
    ],
    [venues]
  )

  const facilityOptions = useMemo(
    () => [
      { label: 'All Surfaces', value: ALL_VALUE },
      ...(selectedVenue?.facilities || []).map((facility) => ({
        value: facility.id,
        label: (
          <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
            {facility.name}
          </Text>
        ),
      })),
    ],
    [selectedVenue?.facilities]
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px 0;
        background-color: ${colors.SECONDARY_500};
        border-radius: 8px;
      `}
    >
      {/* Global styles for AutoComplete and TreeSelect dropdown */}
      <style>
        {`
          .filter-autocomplete-dropdown {
              z-index: 999999 !important;
              background-color: ${colors.SECONDARY_500} !important;
              padding-top: 4px;
              padding-left: 0;
              padding-right: 0;
              padding-bottom: 0;
          }
          .filter-autocomplete-dropdown .ant-select-item-option-content {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
          .filter-autocomplete-dropdown .ant-select-tree {
            background: ${colors.SECONDARY_500} !important;
            color: ${colors.WHITE} !important;
          }
          .filter-autocomplete-dropdown .ant-select-tree-node-content-wrapper {
            color: ${colors.WHITE} !important;
          }
          .filter-autocomplete-dropdown .ant-select-tree-node-content-wrapper:hover {
            background: ${colors.SECONDARY_400} !important;
          }
          .filter-autocomplete-dropdown .ant-select-tree-treenode-selected .ant-select-tree-node-content-wrapper {
            background: ${colors.SECONDARY_400} !important;
          }
          .filter-autocomplete-dropdown .ant-select-tree-switcher {
            color: ${colors.NEUTRAL_50} !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .filter-autocomplete-dropdown .ant-select-tree-indent-unit {
            width: 12px !important;
          }
        `}
      </style>
      {/* Single row: All filters side by side */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          align-items: flex-end;
          flex-wrap: wrap;
          @media (max-width: 1400px) {
            flex-direction: column;
            align-items: stretch;
            padding: 0 16px;
          }
        `}
      >
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;
            max-width: 148px;
            @media (max-width: 1400px) {
              max-width: 100%;
            }
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_0}
          >
            Membership Type
          </Text>
          <MembershipTypeFilter
            selected={selectedMembershipType}
            onSelect={setSelectedMembershipType}
          />
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
            flex: 1;
            min-width: 180px;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.WHITE}
            style={`
              align-self: stretch;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            {isCanlan ? 'Leagues' : 'Organizations'}
          </Text>
          <TreeSelect
            css={filterTreeSelect}
            popupClassName='filter-autocomplete-dropdown'
            labelInValue
            value={
              selectedOrganization
                ? {
                    value: selectedOrganization.id,
                    label:
                      selectedOrganization.name_full ||
                      selectedOrganization.name,
                  }
                : {
                    value: ALL_VALUE,
                    label: isCanlan ? 'All Leagues' : 'All Organizations',
                  }
            }
            placeholder={isCanlan ? 'All Leagues' : 'All Organizations'}
            treeData={organizationsTreeData}
            showSearch
            filterTreeNode={(inputValue, treeNode) => {
              const searchText = (treeNode.title as string)?.toLowerCase() || ''
              return searchText.includes(inputValue.toLowerCase())
            }}
            onChange={(valueObj) => {
              const value = valueObj?.value
              if (value === ALL_VALUE) {
                setSelectedOrganization(null)
              } else if (organizationsTree) {
                const node = findNodeById(organizationsTree, value)
                if (node) {
                  setSelectedOrganization(treeNodeToOrganization(node))
                }
              }
            }}
            allowClear={!shouldDisableOrganization}
            onClear={() => setSelectedOrganization(null)}
            disabled={loadingOrganizations || shouldDisableOrganization}
            treeDefaultExpandedKeys={defaultExpandedKeys}
            treeTitleRender={renderTreeNode}
            popupMatchSelectWidth={false}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            notFoundContent={
              loadingOrganizations ? <Spin size='small' /> : null
            }
          />
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
            flex: 1;
            min-width: 180px;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.WHITE}
            style={`
              align-self: stretch;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            {isCanlan ? 'Seasons' : 'Competitions'}
          </Text>
          <AutoComplete
            popupMatchSelectWidth={false}
            css={filterAutoComplete}
            popupClassName='filter-autocomplete-dropdown'
            value={getScheduleDisplayValue()}
            placeholder={isCanlan ? 'All Seasons' : 'All Competitions'}
            options={scheduleOptions}
            onSearch={handleSchedulesSearch}
            onSelect={(value) => {
              if (value === ALL_VALUE) {
                setSelectedSchedule(null)
                searchSchedules('')
              } else {
                const schedule = schedules.find((s) => s.id === value)
                setSelectedSchedule(schedule || null)
              }
            }}
            onPopupScroll={handleSchedulesScroll}
            filterOption={false}
            allowClear={!shouldDisableSchedule}
            onClear={() => {
              setSelectedSchedule(null)
              searchSchedules('')
            }}
            disabled={loadingSchedules || shouldDisableSchedule}
            open={schedulesDropdownOpen}
            onDropdownVisibleChange={setSchedulesDropdownOpen}
            dropdownRender={(menu) =>
              getDropdownRender(menu, appendingSchedules)
            }
          />
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
            flex: 1;
            min-width: 180px;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.WHITE}
            style={`
              align-self: stretch;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            Teams
          </Text>
          <AutoComplete
            popupMatchSelectWidth={false}
            css={filterAutoComplete}
            popupClassName='filter-autocomplete-dropdown'
            value={getTeamDisplayValue()}
            placeholder={shouldDisableTeam ? 'No Teams' : 'All Teams'}
            options={teamOptions}
            onSearch={handleTeamsSearch}
            onSelect={(value) => {
              if (value === ALL_VALUE) {
                setSelectedTeam(null)
                searchTeams('')
              } else {
                const team = teams.find((t) => t.id === value)
                setSelectedTeam(team || null)
              }
            }}
            onPopupScroll={handleTeamsScroll}
            filterOption={false}
            allowClear={!shouldDisableTeam}
            onClear={() => {
              setSelectedTeam(null)
              searchTeams('')
            }}
            disabled={loadingTeams}
            open={teamsDropdownOpen}
            onDropdownVisibleChange={setTeamsDropdownOpen}
            dropdownRender={(menu) => getDropdownRender(menu, appendingTeams)}
          />
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
            flex: 1;
            min-width: 180px;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.WHITE}
            style={`
              align-self: stretch;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            Locations
          </Text>
          <AutoComplete
            popupMatchSelectWidth={false}
            css={filterAutoComplete}
            popupClassName='filter-autocomplete-dropdown'
            value={getVenueDisplayValue()}
            placeholder='All Locations'
            options={venueOptions}
            onSearch={handleVenuesSearch}
            onSelect={(value) => {
              if (value === ALL_VALUE) {
                setSelectedVenue(null)
                setSelectedFacility(null)
                searchVenues('')
              } else {
                const venue = venues.find((v) => v.id === value)
                setSelectedFacility(null)
                setSelectedVenue(venue || null)
              }
            }}
            onPopupScroll={handleVenuesScroll}
            filterOption={false}
            allowClear={!shouldDisableVenue}
            onClear={() => {
              setSelectedVenue(null)
              setSelectedFacility(null)
              searchVenues('')
            }}
            disabled={loadingVenues || shouldDisableVenue}
            open={venuesDropdownOpen}
            onDropdownVisibleChange={setVenuesDropdownOpen}
            dropdownRender={(menu) => getDropdownRender(menu, appendingVenues)}
          />
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
            flex: 1;
            min-width: 180px;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.WHITE}
            style={`
              align-self: stretch;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            Surfaces
          </Text>
          <AutoComplete
            popupMatchSelectWidth={false}
            css={filterAutoComplete}
            popupClassName='filter-autocomplete-dropdown'
            value={getFacilityDisplayValue()}
            placeholder='All Surfaces'
            options={facilityOptions}
            onSelect={(value) => {
              if (value === ALL_VALUE) {
                setSelectedFacility(null)
              } else {
                const facility = selectedVenue?.facilities?.find(
                  (f) => f.id === value
                )
                setSelectedFacility(facility || null)
              }
            }}
            filterOption={false}
            allowClear={!shouldDisableFacility}
            onClear={() => {
              setSelectedFacility(null)
            }}
            disabled={shouldDisableFacility}
          />
        </div>

        <div
          css={css`
            flex: 1;
            min-width: 280px;
          `}
        >
          <RangePicker
            label='Date Range'
            allowClear={true}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setSelectedDateRange([dates[0], dates[1]])
              } else {
                setSelectedDateRange(null)
              }
            }}
            starts_at={selectedDateRange?.[0] || null}
            ends_at={selectedDateRange?.[1] || null}
            fieldErrors={[]}
            name='dateRange'
            customError={null}
            format={{
              format: 'MM/DD/YYYY',
              type: 'mask',
            }}
            backgroundColor='transparent'
          />
        </div>

        {/* Time Period Filter - disabled when date range is selected */}
        <div
          css={css`
            flex: 1;
            min-width: 300px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: space-between;
            gap: 8px;
            @media (max-width: 768px) {
              width: 100%;
              min-width: 100%;
              flex-direction: row;
            }
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
              gap: 16px;
              align-items: center;
              justify-content: space-between;
              @media (max-width: 768px) {
                width: 100%;
                flex-direction: column;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: row;
                gap: 16px;
                align-items: center;
              `}
            >
              {datesLoaded ? (
                <TimePeriodFilter
                  selected={selectedTimePeriod}
                  onSelect={setSelectedTimePeriod}
                  disabled={!!selectedDateRange}
                />
              ) : (
                <div
                  css={css`
                    width: 300px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    @media (max-width: 768px) {
                      width: 100%;
                    }
                  `}
                />
              )}
              {gamesPagination && gamesPagination?.total > 0 && datesLoaded ? (
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.NEUTRAL_50}
                >
                  {gamesPagination.total.toLocaleString()} Game
                  {gamesPagination.total === 1 ? '' : 's'}
                </Text>
              ) : null}
            </div>
            {/* Sort Order Buttons */}
            {datesLoaded ? (
              <div css={sortOrderContainer}>
                <button
                  type='button'
                  css={sortOrderButtonBase}
                  className={cn({ sorted: selectedSortOrder === 'asc' })}
                  onClick={() => setSelectedSortOrder('asc')}
                >
                  Oldest
                </button>
                <button
                  type='button'
                  css={sortOrderButtonBase}
                  className={cn({ sorted: selectedSortOrder === 'desc' })}
                  onClick={() => setSelectedSortOrder('desc')}
                >
                  Newest
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FilterBar
